import { useState, useMemo } from "react";
import { Icons } from "@admin/components/icons/Icons";
import { Modal } from "@admin/components/ui/Modal";
import { AdminTable } from "@admin/components/shared/AdminTable";
import { Pagination } from "@admin/components/ui/Pagination";
import { CreateCategoryModal } from "@admin/components/modals/CreateCategoryModal";
import { useDebounce, useNotifications } from "@admin/hooks";
import { useCategories, useCreateCategory, useUpdateCategory, useToggleCategoryStatus } from "@admin/hooks";
import { useActiveCatalogs } from "@shared/hooks/useCatalogs";
import type { Category, CreateCategoryData } from "@admin/types/category";

export function AdminCategoriasPage() {
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<Category | undefined>(undefined);
  const [isStatusModalOpen, setIsStatusModalOpen] = useState(false);
  const [categoryToToggle, setCategoryToToggle] = useState<Category | undefined>(undefined);

  const { showSuccess, showError } = useNotifications();

  // Aplicar debounce al searchQuery (500ms de delay)
  const debouncedSearchQuery = useDebounce(searchQuery, 500);

  const {
    data: categoriesResponse,
    isLoading,
    isError,
    error,
  } = useCategories({
    page: currentPage,
    limit: pageSize,
    search: debouncedSearchQuery || undefined,
  });

  const categories: Category[] = categoriesResponse?.data ?? [];
  const paginationMeta = categoriesResponse?.pagination;
  const totalPages = paginationMeta?.totalPages ?? 1;
  const totalItems = paginationMeta?.total ?? categories.length;
  const activePage = paginationMeta?.page ?? currentPage;

  const { data: catalogsData } = useActiveCatalogs();
  const catalogItems = catalogsData?.data?.items ?? [];

  const catalogNameById = useMemo(() => {
    const map = new Map<string, string>();
    (catalogItems ?? []).forEach((catalog: { id: string; name: string }) => {
      if (catalog?.id) {
        map.set(catalog.id, catalog.name);
      }
    });
    return map;
  }, [catalogItems]);

  const categoryNameById = useMemo(() => {
    const map = new Map<string, string>();
    categories.forEach((category) => {
      map.set(category.id, category.name);
    });
    return map;
  }, [categories]);

  // Mutaciones
  const createCategoryMutation = useCreateCategory();
  const updateCategoryMutation = useUpdateCategory();
  const toggleCategoryStatusMutation = useToggleCategoryStatus();

  // Columnas de la tabla
  const columns = [
    {
      header: "NOMBRE",
      accessor: "name" as keyof Category,
    },
    {
      header: "SLUG",
      accessor: "slug" as keyof Category,
    },
    {
      header: "CATÁLOGO",
      accessor: (row: Category) =>
        catalogNameById.get(row.catalogId) ?? row.catalogId,
    },
    {
      header: "PADRE",
      accessor: (row: Category) =>
        row.parentId ? categoryNameById.get(row.parentId) ?? row.parentId : "Raíz",
    },
    {
      header: "TIPO",
      accessor: (row: Category) =>
        row.isRootCategory ? (
          <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
            Raíz
          </span>
        ) : (
          <span className="px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-700">
            Subcategoría
          </span>
        ),
    },
    {
      header: "DESTACADA",
      accessor: (row: Category) =>
        row.featured ? (
          <span className="px-2 py-1 text-xs font-medium rounded-full bg-yellow-100 text-yellow-800">
            Sí
          </span>
        ) : (
          <span className="px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-600">
            No
          </span>
        ),
    },
    {
      header: "ORDEN",
      accessor: (row: Category) => row.order,
    },
    {
      header: "ESTADO",
      accessor: (row: Category) => (
        <span
          className={`px-2 py-1 text-xs font-medium rounded-full ${
            row.active ? "bg-gray-200 text-gray-800" : "bg-red-100 text-red-800"
          }`}
        >
          {row.active ? "Activa" : "Inactiva"}
        </span>
      ),
    },
    {
      header: "CREADA",
      accessor: (row: Category) =>
        new Date(row.createdAt).toLocaleDateString("es-AR"),
    },
  ];

  // Acciones para cada fila
  const renderActions = (category: Category) => (
    <div className="flex space-x-2">
      <button
        onClick={() => handleEdit(category)}
        className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
        title="Editar categoría"
      >
        <Icons.Edit size={16} />
      </button>
      <button
        onClick={() => handleToggleStatus(category)}
        className={`p-2 rounded-lg transition-colors ${
          category.active
            ? "text-red-600 hover:bg-red-50"
            : "text-[#282828] hover:bg-gray-100"
        }`}
        title={category.active ? "Desactivar categoría" : "Activar categoría"}
      >
        {category.active ? <Icons.EyeOff size={16} /> : <Icons.Eye size={16} />}
      </button>
    </div>
  );

  const handleEdit = (category: Category) => {
    setEditingCategory(category);
    setIsModalOpen(true);
  };

  const handleToggleStatus = (category: Category) => {
    setCategoryToToggle(category);
    setIsStatusModalOpen(true);
  };

  const handleConfirmToggleStatus = () => {
    if (categoryToToggle) {
      toggleCategoryStatusMutation.mutate(
        {
          id: categoryToToggle.id,
          currentActive: categoryToToggle.active,
        },
        {
        onSuccess: () => {
          const action = categoryToToggle.active ? "desactivada" : "activada";
          showSuccess(
            "Estado actualizado",
            `La categoría "${categoryToToggle.name}" ha sido ${action} exitosamente.`,
            4000
          );
        },
        onError: () => {
          showError(
            "Error al cambiar estado",
            "No se pudo cambiar el estado de la categoría. Por favor, inténtalo de nuevo.",
            6000
          );
        },
        }
      );
      setIsStatusModalOpen(false);
      setCategoryToToggle(undefined);
    }
  };

  const handleCancelToggleStatus = () => {
    setIsStatusModalOpen(false);
    setCategoryToToggle(undefined);
  };

  const handleAddCategory = () => {
    setEditingCategory(undefined);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingCategory(undefined);
  };

  const handleSubmitCategory = async (data: CreateCategoryData) => {
    try {
      if (editingCategory) {
        await updateCategoryMutation.mutateAsync({ id: editingCategory.id, ...data });
        showSuccess(
          "Categoría actualizada",
          `La categoría \"${data.name}\" se ha actualizado exitosamente.`,
          4000
        );
      } else {
        await createCategoryMutation.mutateAsync(data);
        showSuccess(
          "Categoría creada",
          `La categoría \"${data.name}\" se ha creado exitosamente.`,
          4000
        );
      }

      handleCloseModal();
    } catch {
      showError(
        "Error al guardar categoría",
        "No se pudo guardar la categoría. Por favor, inténtalo de nuevo.",
        6000
      );
    }
  };

  return (
    <div className="space-y-6">
      {/* Tabla de categorías con funcionalidad extendida */}
      <AdminTable
        title="Gestión de Categorías"
        subtitle="Administra las categorías de productos del sistema"
        searchable={true}
        searchValue={searchQuery}
        onSearchChange={(value) => {
          setSearchQuery(value);
          setCurrentPage(1);
        }}
        searchPlaceholder="Buscar categorías..."
        columns={columns}
        data={categories}
        isLoading={isLoading}
        isError={isError}
        error={error?.message || undefined}
        renderActions={renderActions}
        actionsHeader="Acciones"
        maxHeight="500px"
        pagination={
          <Pagination
            currentPage={activePage}
            totalPages={totalPages}
            totalItems={totalItems}
            pageSize={pageSize}
            onPageChange={setCurrentPage}
          />
        }
        onCreateClick={handleAddCategory}
        createButtonText="Agregar Categoría"
      />

      {/* Modal de formulario */}
      <CreateCategoryModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onSubmit={handleSubmitCategory}
        category={editingCategory}
        isLoading={editingCategory ? updateCategoryMutation.isPending : createCategoryMutation.isPending}
      />

      {/* Modal de confirmación de cambio de estado */}
      <Modal
        isOpen={isStatusModalOpen}
        onClose={handleCancelToggleStatus}
        title={
          categoryToToggle?.active
            ? "¿Desactivar categoría?"
            : "¿Activar categoría?"
        }
        size="sm"
      >
        <div className="space-y-4">
          <p className="text-gray-600">
            {categoryToToggle?.name} -{" "}
            {categoryToToggle?.active ? "Activa" : "Inactiva"}
          </p>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              onClick={handleCancelToggleStatus}
              className="px-4 py-2 text-[#282828] bg-white border border-[#282828] rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#282828] focus:border-[#282828] transition-colors"
              disabled={toggleCategoryStatusMutation.isPending}
            >
              Cancelar
            </button>
            <button
              onClick={handleConfirmToggleStatus}
              disabled={toggleCategoryStatusMutation.isPending}
              className="px-4 py-2 text-white bg-[#282828] border border-transparent rounded-md hover:bg-[#3a3a3a] focus:outline-none focus:ring-2 focus:ring-[#282828] focus:border-[#282828] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {toggleCategoryStatusMutation.isPending ? (
                <span className="flex items-center">
                  <svg
                    className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Procesando...
                </span>
              ) : (
                "Confirmar"
              )}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

